import React, { useState, useEffect } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { collection, query, getDocs, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { GA_MEASUREMENT_ID, analyticsWidgets } from '../../config/analytics';
import { GA_CONFIG } from '../../config/googleAnalytics';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './Analytics.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Add this function before the Analytics component
const processGAData = (data) => {
  // Basic processing of Google Analytics data
  return {
    activeUsers: data.activeUsers || [],
    pageViews: data.pageViews || [],
    clickEvents: data.clickEvents || []
  };
};

const Analytics = () => {
  const [selectedWidgets, setSelectedWidgets] = useState(() => {
    const saved = localStorage.getItem('selectedWidgets');
    return saved ? JSON.parse(saved) : [
      'activeUsers',
      'pageViews',
      'clickEvents',
      'providerSignups',
      'categoryEngagement',
      'revenue'
    ];
  });
  const [timeframe, setTimeframe] = useState('week');
  const [analyticsData, setAnalyticsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCustomizing, setIsCustomizing] = useState(false);

  useEffect(() => {
    loadAnalytics();
    // Save selected widgets to localStorage
    localStorage.setItem('selectedWidgets', JSON.stringify(selectedWidgets));
  }, [selectedWidgets, timeframe]);

  const fetchFirebaseData = async () => {
    try {
      // Get providers from both collections
      const providersRef = collection(db, 'providers');
      const serviceProvidersRef = collection(db, 'serviceProviders');
      
      const [providersSnapshot, serviceProvidersSnapshot] = await Promise.all([
        getDocs(providersRef),
        getDocs(serviceProvidersRef)
      ]);

      // Log raw counts for debugging
      console.log('Raw counts:', {
        providers: providersSnapshot.docs.length,
        serviceProviders: serviceProvidersSnapshot.docs.length
      });

      // Only include documents that have the essential provider fields
      const validProviders = providersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(provider => 
          provider.businessName || 
          provider.name || 
          provider.category
        );

      const validServiceProviders = serviceProvidersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(provider => 
          provider.businessName || 
          provider.name || 
          provider.category
        );

      // Log filtered counts
      console.log('Filtered counts:', {
        validProviders: validProviders.length,
        validServiceProviders: validServiceProviders.length
      });

      // Combine and deduplicate based on business name or ID
      const uniqueProviders = new Map();
      
      [...validProviders, ...validServiceProviders].forEach(provider => {
        const key = provider.businessName || provider.name || provider.id;
        if (!uniqueProviders.has(key)) {
          uniqueProviders.set(key, provider);
        }
      });

      const providerData = Array.from(uniqueProviders.values());
      console.log('Final unique providers count:', providerData.length);

      // Log sample of data for verification
      console.log('Sample of first 5 providers:', providerData.slice(0, 5));

      return {
        providerSignups: processProviderData(providerData)
      };
    } catch (error) {
      console.error('Error fetching Firebase data:', error);
      return {};
    }
  };

  const processClickData = (data) => {
    const groupedData = data.reduce((acc, click) => {
      let date;
      if (click.timestamp?.toDate) {
        date = click.timestamp.toDate().toLocaleDateString();
      } else if (click.timestamp) {
        date = new Date(click.timestamp).toLocaleDateString();
      } else {
        date = 'Unknown Date';
      }
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(groupedData),
      values: Object.values(groupedData)
    };
  };

  const processUserData = (data) => {
    const groupedData = data.reduce((acc, user) => {
      let date;
      if (user.createdAt?.toDate) {
        date = user.createdAt.toDate().toLocaleDateString();
      } else if (user.createdAt) {
        date = new Date(user.createdAt).toLocaleDateString();
      } else {
        date = 'Unknown Date';
      }
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(groupedData),
      values: Object.values(groupedData)
    };
  };

  const processProviderData = (data) => {
    // Create a map to store daily counts
    const dailyCounts = new Map();

    data.forEach(provider => {
      try {
        let date;
        if (provider.createdAt?.toDate) {
          date = provider.createdAt.toDate();
        } else if (provider.createdAt) {
          date = new Date(provider.createdAt);
        } else {
          return; // Skip if no valid date
        }

        // Skip future dates
        if (date > new Date()) return;

        const dateStr = date.toLocaleDateString();
        dailyCounts.set(dateStr, (dailyCounts.get(dateStr) || 0) + 1);
      } catch (error) {
        console.error('Error processing provider:', provider.id, error);
      }
    });

    // Convert to sorted arrays
    const sortedDates = Array.from(dailyCounts.keys()).sort((a, b) => 
      new Date(a) - new Date(b)
    );

    const result = {
      labels: sortedDates,
      values: sortedDates.map(date => dailyCounts.get(date))
    };

    // Final verification log
    console.log('Processed data:', {
      totalProviders: data.length,
      uniqueDates: sortedDates.length,
      totalSignups: sortedDates.reduce((sum, date) => sum + dailyCounts.get(date), 0)
    });

    return result;
  };

  const fetchGoogleAnalytics = async () => {
    try {
      const startDate = getStartDate(timeframe);
      
      const response = await fetch('/api/ga-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate,
          endDate: 'today',
          credentials: GA_CONFIG.credentials
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch GA data');
      }

      const data = await response.json();
      return processGAData(data);
    } catch (error) {
      console.error('Error fetching GA data:', error);
      return {};
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // For now, let's only use Firebase data and skip GA
      const firebaseData = await fetchFirebaseData();
      setAnalyticsData(firebaseData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
    setLoading(false);
  };

  const renderChart = (type, data) => {
    if (!data) return <div>No data available</div>;

    const chartData = {
      labels: data.labels || data.map((_, i) => `Day ${i + 1}`),
      datasets: [{
        label: 'Value',
        data: data.values || data,
        backgroundColor: type === 'pie' 
          ? ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF']
          : '#f0a500',
        borderColor: type === 'line' ? '#f0a500' : undefined,
        borderWidth: 2,
        fill: false
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: type === 'pie',
          position: 'bottom'
        }
      },
      scales: type !== 'pie' ? {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      } : undefined
    };

    switch (type) {
      case 'line':
        return <Line data={chartData} options={options} />;
      case 'bar':
        return <Bar data={chartData} options={options} />;
      case 'pie':
        return <Pie data={chartData} options={options} />;
      default:
        return <div>Unsupported chart type</div>;
    }
  };

  const getStartDate = (timeframe) => {
    const date = new Date();
    switch (timeframe) {
      case 'day':
        date.setDate(date.getDate() - 1);
        break;
      case 'week':
        date.setDate(date.getDate() - 7);
        break;
      case 'month':
        date.setMonth(date.getMonth() - 1);
        break;
      case 'year':
        date.setFullYear(date.getFullYear() - 1);
        break;
      default:
        date.setDate(date.getDate() - 7);
    }
    return date.toISOString().split('T')[0];
  };

  const renderWidget = (widgetId) => {
    const widget = Object.values(analyticsWidgets)
      .flat()
      .find(w => w.id === widgetId);

    if (!widget) return null;

    const data = analyticsData[widgetId];
    
    return (
      <div className="widget" key={widgetId}>
        <div className="widget-header">
          <h3>{widget.name}</h3>
          <select 
            value={timeframe} 
            onChange={(e) => setTimeframe(e.target.value)}
            className="timeframe-select"
          >
            <option value="day">24 Hours</option>
            <option value="week">7 Days</option>
            <option value="month">30 Days</option>
            <option value="year">12 Months</option>
          </select>
        </div>
        <div className="widget-content">
          {renderChart(widget.type, data)}
        </div>
      </div>
    );
  };

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-header">
        <h2>Analytics Dashboard</h2>
        <button 
          className="customize-btn"
          onClick={() => setIsCustomizing(!isCustomizing)}
        >
          {isCustomizing ? 'Save Layout' : 'Customize'}
        </button>
      </div>
      <div className="widgets-grid">
        {selectedWidgets.map(renderWidget)}
      </div>
    </div>
  );
};

export default Analytics; 